import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

/**
 * Generic HintWrapper Component
 * 
 * Consolidates common hint logic across all game hint components:
 * - Unlock threshold management (3 attempts)
 * - State-based conditional rendering (locked/unlockable/revealed)
 * - Consistent UI patterns and accessibility
 * 
 * Game-specific configurations:
 * - cssPrefix: CSS class prefix for styling
 * - hintProperty: Property name to access hint content (e.g., 'hint', 'verse', 'description')
 * - hintLabel: Display label for the hint (e.g., "Exact Cost", "Key Verse", "Description")
 * - showPostWinHint: Whether to show hint content after winning (default: true)
 */
const HintWrapper = ({
  // Game state props
  attemptsCount,
  currentItem,
  gameWon,
  isHintRevealed,
  onRevealHint,
  
  // Configuration props
  cssPrefix,
  hintProperty,
  hintLabel,
  showPostWinHint = true,
  unlockThreshold = 3,
  
  // Optional custom content renderer
  children,
  ...otherProps
}) => {
  
  const renderHintContent = useCallback(() => {
    if (children) {
      // Use custom content if provided
      return children;
    }
    
    // Default content rendering using hintProperty
    if (!currentItem || !hintProperty || !currentItem[hintProperty]) {
      return null;
    }
    
    return (
      <>
        <div><strong>{hintLabel}:</strong></div>
        <div className={`${cssPrefix}__hint-content`}>{currentItem[hintProperty]}</div>
      </>
    );
  }, [children, currentItem, hintProperty, hintLabel, cssPrefix]);

  // Don't render anything if no current item
  if (!currentItem) {
    return null;
  }

  // Show hint after winning (if enabled for this game)
  if (gameWon && showPostWinHint) {
    // Show hint if it was revealed
    if (isHintRevealed) {
      return (
        <div className={`${cssPrefix}__hint ${cssPrefix}__hint--revealed`} {...otherProps}>
          {renderHintContent()}
        </div>
      );
    }
    // Show "was available" message if hint was unlocked but not revealed
    if (attemptsCount >= unlockThreshold) {
      return (
        <div className={`${cssPrefix}__hint ${cssPrefix}__hint--locked`} {...otherProps}>
          <span>Hint was available.</span>
        </div>
      );
    }
    // Don't show anything if hint wasn't even unlocked
    return null;
  }

  // Show revealed hint content
  if (isHintRevealed) {
    return (
      <div className={`${cssPrefix}__hint ${cssPrefix}__hint--revealed`} {...otherProps}>
        {renderHintContent()}
      </div>
    );
  }

  // Show "locked" state (fewer than threshold attempts)
  if (attemptsCount < unlockThreshold) {
    const remainingAttempts = unlockThreshold - attemptsCount;
    return (
      <div className={`${cssPrefix}__hint ${cssPrefix}__hint--locked`} {...otherProps}>
        <div className={`${cssPrefix}__hint-locked-message`}>
          <span>Hint unlocks in {remainingAttempts} guess{remainingAttempts !== 1 ? 'es' : ''}.</span>
        </div>
      </div>
    );
  }

  // Show "unlockable" state (can reveal hint)
  return (
    <div className={`${cssPrefix}__hint ${cssPrefix}__hint--unlockable`} {...otherProps}>
      <button 
        className={`${cssPrefix}__hint-reveal-button`}
        onClick={onRevealHint}
        type="button"
        aria-label={`Reveal ${hintLabel.toLowerCase()}`}
      >
        Reveal {hintLabel}
      </button>
    </div>
  );
};

HintWrapper.propTypes = {
  // Game state props (required)
  attemptsCount: PropTypes.number.isRequired,
  currentItem: PropTypes.object,
  gameWon: PropTypes.bool.isRequired,
  isHintRevealed: PropTypes.bool.isRequired,
  onRevealHint: PropTypes.func.isRequired,
  
  // Configuration props (required for default behavior)
  cssPrefix: PropTypes.string.isRequired,
  hintProperty: PropTypes.string,
  hintLabel: PropTypes.string.isRequired,
  showPostWinHint: PropTypes.bool,
  unlockThreshold: PropTypes.number,
  
  // Optional custom content
  children: PropTypes.node
};

export default HintWrapper;